import React from "react";
import { cn } from "@/utils/cn";

export type ButtonVariant = "solid" | "outline" | "soft" | "text" | "white";
export type ButtonSize = "lg" | "md" | "sm" | "xs";

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const SIZE_CLASSES: Record<ButtonSize, string> = {
  lg: "h-12 px-6 gap-2 rounded-[10px] body-a-semibold",
  md: "h-10 px-5 gap-2 rounded-lg body-b-semibold",
  sm: "h-8 px-4 gap-2 rounded-md caption-semibold",
  xs: "h-6 px-2 gap-1.5 rounded overline-semibold",
};

const SIZE_ICON_CLASSES: Record<ButtonSize, string> = {
  lg: "size-6",
  md: "size-5",
  sm: "size-4",
  xs: "size-4",
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  solid: "bg-primary-500 text-white hover:bg-primary-400 disabled:opacity-30",
  outline:
    "border border-primary-500 text-primary-500 hover:border-primary-400 hover:text-primary-400 disabled:opacity-30",
  soft: "bg-primary-50 text-primary-500 hover:bg-primary-100 disabled:opacity-30",
  text: "text-primary-500 hover:text-primary-400 disabled:opacity-30",
  white: "bg-white text-primary-500 hover:bg-[#fafafa] disabled:opacity-30",
};

const BASE =
  "inline-flex items-center justify-center transition-colors duration-200 disabled:cursor-not-allowed";

export const Button: React.FC<ButtonProps> = ({
  variant = "solid",
  size = "md",
  iconLeft,
  iconRight,
  children,
  className,
  ...props
}) => (
  <button
    className={cn(BASE, SIZE_CLASSES[size], VARIANT_CLASSES[variant], className)}
    {...props}
  >
    {iconLeft && (
      <span className={`shrink-0 ${SIZE_ICON_CLASSES[size]}`}>{iconLeft}</span>
    )}
    {children}
    {iconRight && (
      <span className={`shrink-0 ${SIZE_ICON_CLASSES[size]}`}>{iconRight}</span>
    )}
  </button>
);
